import { ColumnLayoutType, CornerStyle, DiscDisplayMode, PrintUnitType, TextAlignment, TextDirection } from "./enums";

/**
 * 인쇄부 치수 타입
 * - 인쇄부 영역의 물리적 크기 (mm)
 */
export interface PrintUnitDimension {
  width: number; // 가로 크기 (mm)
  height: number; // 세로 크기 (mm)
}

/**
 * 코너 설정 타입
 * - 각 모서리 설정
 */
export interface CornerSettings {
  topLeft: {
    style: CornerStyle;
    size: number; // mm 단위
  };
  topRight: {
    style: CornerStyle;
    size: number; // mm 단위
  };
  bottomLeft: {
    style: CornerStyle;
    size: number; // mm 단위
  };
  bottomRight: {
    style: CornerStyle;
    size: number; // mm 단위
  };
}

/**
 * 배경 이미지 설정 타입
 */
export interface BackgroundSettings {
  image: string | null; // base64 문자열 또는 null
  scale: number; // 확대/축소 비율
  x: number; // 좌표 이동 (px)
  y: number; // 좌표 이동 (px)
  color: string; // 배경색 (앨범 이미지가 없을 때)
  rotation?: number; // 회전 각도 (도)
  transparent?: boolean; // 투명 배경 여부
}

/**
 * 텍스트 설정 타입
 */
export interface TextSettings {
  color: string;
  size: number; // 폰트 크기 (px)
  autoSize: boolean; // 자동 폰트 크기 조절 여부
  alignment: TextAlignment;
  direction: TextDirection;
  fontFamily: string;
  paddingLeft: number; // 좌측 여백 (px)
  paddingRight: number; // 우측 여백 (px)
}

/**
 * 이미지 필터 설정 타입
 */
export interface ImageFilter {
  brightness: number; // 밝기 (0~200, 기본 100)
  contrast: number;   // 대비 (0~200, 기본 100)
  saturation: number; // 채도 (0~200, 기본 100)
  blur: number;       // 블러 (0~20px, 기본 0)
}

/**
 * 로고 위치 타입
 */
export interface LogoPosition {
  x: number;
  y: number;
  scale?: number; // 로고 크기 비율 (1.0이 기본)
}

/**
 * 공통 인쇄부 인터페이스
 */
export interface BasePrintUnit {
  type: PrintUnitType;
  name: string;
  dimension: PrintUnitDimension;
  background: BackgroundSettings;
  cornerSettings: CornerSettings;
  showMdLogo: boolean; // 미니디스크 로고 표시 여부
  logoPosition?: LogoPosition; // 미니디스크 로고 위치 (드래그로 이동 가능)
  logoRotation?: number; // 미니디스크 로고 회전 각도 (도)
  logoScale?: number; // 미니디스크 로고 크기 비율 (1.0이 기본)
  logoX?: number; // 미니디스크 로고 X 좌표
  logoY?: number; // 미니디스크 로고 Y 좌표
  useBackground: boolean; // 배경 이미지 사용 여부
  useText: boolean; // 텍스트 사용 여부
  imageFilter?: ImageFilter; // 이미지 필터 설정
}

/**
 * 전면 커버 인쇄부 (인쇄부 1)
 */
export interface FrontCoverUnit extends BasePrintUnit {
  type: PrintUnitType.FRONT;
  text: TextSettings; // 텍스트 설정 추가
  showTitle: boolean; // 앨범명 표시 여부
  showArtist: boolean; // 가수명 표시 여부
  titlePosition?: { x: number; y: number }; // 앨범명 위치 (드래그로 이동 가능)
  artistPosition?: { x: number; y: number }; // 가수명 위치 (드래그로 이동 가능)
  titleFontSize?: number; // 앨범명 글자 크기
  artistFontSize?: number; // 가수명 글자 크기
}

/**
 * 측면 인쇄부 (인쇄부 2)
 */
export interface SideUnit extends BasePrintUnit {
  type: PrintUnitType.SIDE;
  text: TextSettings;
  content: string; // 가수명 + 앨범명
  blurIntensity?: number; // 블러 강도 설정 (px 단위)
}

/**
 * 뒷면 상단 인쇄부 (인쇄부 3)
 */
export interface BackTopUnit extends BasePrintUnit {
  type: PrintUnitType.BACK_TOP;
  text: TextSettings;
  content: string; // 앨범명
  useTwoLines: boolean; // 두 줄 표시 여부
  blurIntensity?: number; // 블러 강도 설정 (px 단위)
}

/**
 * 뒷면 곡 리스트 인쇄부 (인쇄부 4)
 */
export interface BackTracklistUnit extends BasePrintUnit {
  type: PrintUnitType.BACK;
  text: TextSettings;
  columnLayout: ColumnLayoutType; // 열 배치 방식
  lineHeight: number; // 줄 간격
  blurIntensity: number; // 블러 강도 설정 (px 단위)
  useTwoColumns?: boolean; // 이전 버전 호환성을 위한 옵션 (deprecated)
  // 흰색 투명도 레이어 설정
  showWhiteOverlay: boolean; // 흰색 투명도 레이어 표시 여부
  whiteOverlayOpacity: number; // 흰색 투명도 (0.0 ~ 1.0)
}

/**
 * 디스크 본체 2 인쇄부 (인쇄부 5)
 */
export interface DiscUnit2 extends BasePrintUnit {
  type: PrintUnitType.DISC2;
  text: TextSettings;
  displayMode: DiscDisplayMode;
  blurIntensity?: number; // 블러 강도 설정 (px 단위)
  content?: string; // 표시할 텍스트 내용 (앨범명/가수명)
  showTitle: boolean; // 앨범명 표시 여부
  showArtist: boolean; // 가수명 표시 여부
  titlePosition?: { x: number; y: number }; // 앨범명 위치 (드래그로 이동 가능)
  artistPosition?: { x: number; y: number }; // 가수명 위치 (드래그로 이동 가능)
  titleFontSize?: number; // 앨범명 글자 크기
  artistFontSize?: number; // 가수명 글자 크기
}

/**
 * 디스크 측면 인쇄부 (인쇄부 6)
 */
export interface DiscSideUnit extends BasePrintUnit {
  type: PrintUnitType.DISC_SIDE;
  text: TextSettings;
  content: string;
  useBackground: boolean;
  useText: boolean;
  blurIntensity?: number; // 블러 강도 설정 (px 단위)
}

/**
 * 모든 인쇄부 타입의 통합 타입
 */
export type PrintUnit = FrontCoverUnit | SideUnit | BackTopUnit | BackTracklistUnit | DiscUnit2 | DiscSideUnit;

/**
 * 인쇄부 초기 치수 상수 맵
 * - 각 인쇄부별 물리적 크기 (mm)
 */
export const PRINT_UNIT_DIMENSIONS: Record<PrintUnitType, PrintUnitDimension> = {
  [PrintUnitType.FRONT]: { width: 72, height: 66 },
  [PrintUnitType.SIDE]: { width: 72, height: 6 },
  [PrintUnitType.BACK_TOP]: { width: 72, height: 9 },
  [PrintUnitType.BACK]: { width: 72, height: 54 },
  [PrintUnitType.DISC2]: { width: 37, height: 52 },
  [PrintUnitType.DISC_SIDE]: { width: 58, height: 3.5 }
};

/**
 * 인쇄 보정 상수 (mm)
 * - 인쇄 영역을 확장하기 위한 보정값
 */
export const PRINT_CORRECTION = {
  width: 2.3, // 가로 보정 (mm)
  height: 2.3 // 세로 보정 (mm)
};

/**
 * 인쇄용지 기본 크기 설정 (mm)
 * - 모든 내보내기 관련 작업에서 참조하는 표준 크기
 * - 보정상수를 더한 확장된 크기로 설정
 */
export const PRINT_PAPER_SIZE = {
  width: 72 + PRINT_CORRECTION.width, // 인쇄용지 너비 (mm) + 보정
  height: 72 + PRINT_CORRECTION.height // 인쇄용지 높이 (mm) + 보정
};

/**
 * 인쇄부 사이 간격 (mm)
 * - 인쇄부들이 나란히 배치될 때 적용되는 표준 간격
 */
export const PRINT_UNIT_GAP_WIDTH = 0.5; // 인쇄부 사이 간격 (mm) 